import {
  DoorPositionMappinDto,
  HubDetailsDto,
  HubDetailsKioskDto,
  KnownHubDetailsDtoStatus,
  KnownHubDetailsDtoType,
  KnownHubDtoType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { SVGIconComponent } from '../../../ui/components/pure/icons/types';
import { Colors } from '../../../ui/theme/colors';
import { SectionDetails } from './section-details.model';
import { Space } from './space.model';
import { TowerDetails } from './tower-details.model';
import { sortByLockerSize } from '../logic/locker-ui.constants';
import { Pause } from '../../../ui/components/pure/icons/Pause.icon';
import { Deleted } from '../../../ui/components/pure/icons/Deleted.icon';
import { Yes } from '../../../ui/components/pure/icons/Yes.icon';
import { LockerDetails } from './locker-details.model';

export class HubDetails {
  public readonly id: string;
  public readonly type: KnownHubDetailsDtoType;
  public readonly name: string;
  public readonly status: KnownHubDetailsDtoStatus;
  public readonly kiosk?: HubDetailsKioskDto;
  public readonly towers?: TowerDetails[];
  public readonly sections?: SectionDetails[];
  public readonly doorPositionMappings?: DoorPositionMappinDto[];

  constructor(hubDetails: HubDetailsDto) {
    this.id = hubDetails.id;
    this.type = hubDetails.type as KnownHubDetailsDtoType;
    this.name = hubDetails.name;
    this.status = hubDetails.status as KnownHubDetailsDtoStatus;
    this.kiosk = hubDetails.kiosk;
    this.doorPositionMappings = hubDetails.doorPositionMappings;
    this.towers = hubDetails.towers
      .sort((a, b) => a.tower.boardNumber - b.tower.boardNumber)
      .map(
        (tower) =>
          new TowerDetails(
            tower.tower,
            tower.lockers.map(
              (locker) =>
                new LockerDetails(
                  locker.locker,
                  locker.doors.map((door) => door.door),
                  tower.tower.dimensions
                )
            ),
            hubDetails.type as KnownHubDtoType
          )
      );
    this.sections = hubDetails.sections
      .sort((a, b) => a.section.sequenceNumber - b.section.sequenceNumber)
      .map(
        (section) =>
          new SectionDetails(
            section.section,
            section.spaces.map((space) => new Space(space.space)),
            section.accessControl,
            hubDetails.type as KnownHubDtoType
          )
      );
  }

  public get statusColor(): string {
    const map: Record<KnownHubDetailsDtoStatus, string> = {
      active: Colors.collectedGreen,
      deleted: Colors.cancelledDeletedRed,
      inactive: Colors.cancelledDeletedRed
    };
    return map[this.status];
  }

  public get statusIcon() {
    const map: Record<KnownHubDetailsDtoStatus, SVGIconComponent> = {
      [KnownHubDetailsDtoStatus.Active]: Yes,
      [KnownHubDetailsDtoStatus.Inactive]: Pause,
      [KnownHubDetailsDtoStatus.Deleted]: Deleted
    };
    return map[this.status];
  }

  public get maxModuleHeight(): number {
    const modules = [...(this.towers ?? []), ...(this.sections ?? [])];
    if (modules.length === 0) {
      return 0;
    }
    return Math.max(...modules.map((section) => section.pixelDimensions2D.heightNumber));
  }

  public get spacesCount() {
    return (this.sections || []).reduce((acc, section) => {
      const usableSpacesCount = section.spaces.filter((s) => !s.isKiosk).length;
      return acc + usableSpacesCount;
    }, 0);
  }

  public get lockersCount() {
    return (this.towers || []).reduce((acc, tower) => {
      const usableLockersCount = tower.lockers.filter((s) => !s.isKiosk).length;
      return acc + usableLockersCount;
    }, 0);
  }

  public get deliveryContainersCount() {
    return this.lockersCount + this.spacesCount;
  }

  public get deliveryContainerType() {
    return [KnownHubDetailsDtoType.LockersHub, KnownHubDetailsDtoType.LockersHubExternal].includes(this.type)
      ? 'locker'
      : 'space';
  }

  public get deliveryContainerCountsBySize() {
    const lockerCountsBySize = (this.towers || []).reduce((acc, tower) => {
      const lockerCounts = tower.lockersCountBySize;
      Object.keys(lockerCounts).forEach((size) => {
        if (acc[size]) {
          acc[size].count += lockerCounts[size].count;
        } else {
          acc[size] = lockerCounts[size];
        }
      });
      return acc;
    }, {} as Record<string, { Icon: SVGIconComponent; count: number }>);

    const spacesCountBySize = (this.sections || []).reduce((acc, section) => {
      const spacesCount = section.spacesCountBySize;
      Object.keys(spacesCount).forEach((size) => {
        if (acc[size]) {
          acc[size].count += spacesCount[size].count;
        } else {
          acc[size] = spacesCount[size];
        }
      });
      return acc;
    }, {} as Record<string, { Icon: SVGIconComponent; count: number }>);

    return {
      ...lockerCountsBySize,
      ...spacesCountBySize
    };
  }

  public get deliveryContainerCountsSortedBySize() {
    const sortedBySize = sortByLockerSize(Object.entries(this.deliveryContainerCountsBySize), ([size]) => size);
    const filteredForZeroValues = sortedBySize.filter(([, val]) => val.count > 0);
    return filteredForZeroValues.map(([key, val]) => ({
      key,
      ...val
    }));
  }

  public get isDoubleSided() {
    return this.towers?.some((t) => t.isDoubleSided) ?? false;
  }
}
